import React, { useContext, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link as RouterLink, useLocation, useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  Avatar,
  Badge,
  Box,
  Button,
  Divider,
  Drawer,
  Hidden,
  List,
  Typography,
  Tooltip,
} from '@mui/material';
import PersonOutlineOutlined from '@mui/icons-material/PersonOutlineOutlined';
import BusinessCenterOutlined from '@mui/icons-material/BusinessCenterOutlined';
import GroupsOutlined from '@mui/icons-material/GroupsOutlined';
import AccountBalanceWalletOutlined from '@mui/icons-material/AccountBalanceWalletOutlined';
import StorefrontOutlined from '@mui/icons-material/StorefrontOutlined';
import InsightsOutlined from '@mui/icons-material/InsightsOutlined';
import SettingsOutlined from '@mui/icons-material/SettingsOutlined';
import ReceiptLongOutlined from '@mui/icons-material/ReceiptLongOutlined';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import NavItem from './NavItem';
import { PassportAuth } from '../../contexts/AuthContext';
import { useSidebarCollapse } from '../../contexts/SidebarContext';
import { getStorage } from '../../utils/functions/localstorage';
import { accessRightSubMenu } from '../../utils/functions/accessRight';
import {
  SIDEBAR_NAV_ITEMS,
  SIDEBAR_MODULE_TITLE,
} from '../../constants/sidebarNavConfig';
import * as actions from '../../redux/actions';
import { useSocket } from '../../contexts/SocketContext';

/** ไอคอนหนึ่งต่อโมดูล — แถบซ้ายหลักเท่านั้น (อ่านง่าย) */
const SIDEBAR_MODULE_ICONS = {
  PROFILE: PersonOutlineOutlined,
  PROJECT: BusinessCenterOutlined,
  HUMEN: GroupsOutlined,
  FINANCE: AccountBalanceWalletOutlined,
  SALE: ReceiptLongOutlined,
  CUSTOMER: StorefrontOutlined,
  DIRECTOR: InsightsOutlined,
  MANAGEMENT: SettingsOutlined,
};

const SIDEBAR_MODULE_SHORT_TITLE = {
  PROFILE: 'โปรไฟล์',
  PROJECT: 'โปรเจกต์',
  HUMEN: 'บุคคล',
  FINANCE: 'การเงิน',
  SALE: 'ขาย',
  CUSTOMER: 'ลูกค้า',
  DIRECTOR: 'ผู้บริหาร',
  MANAGEMENT: 'ตั้งค่า',
};

const items = SIDEBAR_NAV_ITEMS;

const DashboardSidebar = ({ me, onMobileClose, openMobile }) => {
  const {
    firstname = '',
    lastname = '',
    department: { name: departmentName = '' } = {},
  } = JSON.parse(getStorage('remember') || '{}');

  const { handleSignout } = useContext(PassportAuth);
  const { collapsed, toggleSidebar } = useSidebarCollapse();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const [activeModuleName, setActiveModuleName] = React.useState('');

  const pendingLeaveCount = useSelector((state) => state.leaveRequest?.pendingCount || 0);
  const pendingOtCount = useSelector((state) => state.otRequest?.pendingCount || 0);
  const pendingExpensesCount = useSelector((state) => state.expenses?.pendingCount || 0);
  const pendingTimestampApprovalCount = useSelector((state) => state.timestamp?.pendingApprovalCount || 0);
  const pendingEmployeeCount = useSelector((state) => state.employee?.pendingCount || 0);
  const socket = useSocket();

  const onSignOut = async () => {
    await handleSignout();
    history.push('/login');
  };

  useEffect(() => {
    if (openMobile && onMobileClose) {
      onMobileClose();
    }
  }, [location.pathname]);

  const moduleGroups = React.useMemo(() => {
    const groups = {};
    items.forEach((item) => {
      const rowVisible = item.modulePermission
        ? Boolean(me?.userData?.permissions?.[item.modulePermission]) ||
          accessRightSubMenu(me, item.name, item.href, 0)
        : item.sub && item.sub.length > 0
          ? item.sub.some((s) => accessRightSubMenu(me, item.name, s.href, s.level ?? 0))
          : accessRightSubMenu(me, item.name, item.href, 0);
      if (!rowVisible) return;
      if (!groups[item.name]) {
        const ModuleIcon =
          SIDEBAR_MODULE_ICONS[item.name] || SettingsOutlined;
        groups[item.name] = {
          name: item.name,
          title: SIDEBAR_MODULE_TITLE[item.name] || item.title,
          moduleIcon: ModuleIcon,
          href: item.href,
          items: [],
        };
      }
      groups[item.name].items.push(item);
    });
    return Object.values(groups);
  }, [me]);

  const hasHumenAccess = React.useMemo(
    () => moduleGroups.some((m) => m.name === 'HUMEN'),
    [moduleGroups],
  );

  const hasFinanceAccess = React.useMemo(
    () => moduleGroups.some((m) => m.name === 'FINANCE'),
    [moduleGroups],
  );

  useEffect(() => {
    if (!hasHumenAccess) return;
    dispatch(actions.leaveRequestPendingCount());
    dispatch(actions.otRequestPendingCount());
    dispatch(actions.timestampPendingApprovalCount());
    dispatch(actions.employeePendingCount());
    const timer = setInterval(() => {
      dispatch(actions.leaveRequestPendingCount());
      dispatch(actions.otRequestPendingCount());
      dispatch(actions.timestampPendingApprovalCount());
      dispatch(actions.employeePendingCount());
    }, 120000);
    return () => clearInterval(timer);
  }, [hasHumenAccess]);

  useEffect(() => {
    if (!hasFinanceAccess) return;
    dispatch(actions.expensesPendingCount());
    const timer = setInterval(() => dispatch(actions.expensesPendingCount()), 120000);
    return () => clearInterval(timer);
  }, [hasFinanceAccess]);

  useEffect(() => {
    if (!socket) return;
    const refreshExpenses = () => dispatch(actions.expensesPendingCount());
    const refreshLeave = () => dispatch(actions.leaveRequestPendingCount());
    const refreshOt = () => dispatch(actions.otRequestPendingCount());
    const refreshTimestampApproval = () => dispatch(actions.timestampPendingApprovalCount());
    const refreshEmployee = ({ pendingCount }) => dispatch({ type: 'EMPLOYEE_PENDING_COUNT', payload: pendingCount });
    socket.on('expenses:created', refreshExpenses);
    socket.on('expenses:updated', refreshExpenses);
    socket.on('leaveRequest:updated', refreshLeave);
    socket.on('otRequest:updated', refreshOt);
    socket.on('timestamp:created', refreshTimestampApproval);
    socket.on('timestamp:updated', refreshTimestampApproval);
    socket.on('onboarding:submitted', refreshEmployee);
    return () => {
      socket.off('expenses:created', refreshExpenses);
      socket.off('expenses:updated', refreshExpenses);
      socket.off('leaveRequest:updated', refreshLeave);
      socket.off('otRequest:updated', refreshOt);
      socket.off('timestamp:created', refreshTimestampApproval);
      socket.off('timestamp:updated', refreshTimestampApproval);
      socket.off('onboarding:submitted', refreshEmployee);
    };
  }, [socket]);

  useEffect(() => {
    const pathMatch = (href) =>
      location.pathname === href || location.pathname.startsWith(href + '/');
    const found = moduleGroups.find((m) =>
      m.items.some(
        (item) =>
          pathMatch(item.href) ||
          (item?.sub && item.sub.some((s) => pathMatch(s.href))),
      ),);
    if (found) {
      setActiveModuleName(found.name);
    } else if (moduleGroups.length > 0 && !activeModuleName) {
      setActiveModuleName(moduleGroups[0].name);
    }
  }, [location.pathname, moduleGroups]);

  const activeGroup = moduleGroups.find((m) => m.name === activeModuleName);

  const content = (
    <Box
      sx={{
        display: 'flex',
        height: '100%',
        backgroundColor: 'background.paper',
        overflow: 'hidden',
        boxShadow: '4px 0 24px rgba(0,0,0,0.05)',
      }}
    >
      {/* Primary rail — ไอคอนโมดูล */}
      <Box
        sx={{
          width: 80,
          backgroundColor: '#1e1b4b',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          py: 3,
          zIndex: 10,
          boxShadow: '4px 0 10px rgba(0,0,0,0.1)',
        }}
      >
        <Box sx={{ mb: 4 }}>
          <Avatar
            sx={{
              width: 48,
              height: 48,
              cursor: 'pointer',
              border: '2px solid rgba(255,255,255,0.2)',
              transition: 'transform 0.2s',
              '&:hover': { transform: 'scale(1.05)', borderColor: '#fff' },
            }}
            src={me?.userData?.image?.url}
            alt={firstname}
          />
        </Box>
        <Box
          sx={{
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
            overflowY: 'auto',
            width: '100%',
            alignItems: 'center',
            '::-webkit-scrollbar': { display: 'none' },
          }}
        >
          {moduleGroups.map((mod) => {
            const ModuleIcon = mod.moduleIcon;
            const humenPendingTotal = pendingLeaveCount + pendingOtCount + pendingTimestampApprovalCount;
            const moduleBadgeCount =
              mod.name === 'HUMEN' ? humenPendingTotal
              : mod.name === 'FINANCE' ? pendingExpensesCount
              : 0;
            const showBadge = moduleBadgeCount > 0;
            const isActive = activeModuleName === mod.name;
            return (
              <Tooltip key={mod.name} title={mod.name} placement="right">
              <Box
                onClick={() => {
                  setActiveModuleName(mod.name);
                  // ไม่ navigate จาก icon — ให้คลิก sub-item บน secondary rail เสมอ
                }}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 0.5,
                  cursor: 'pointer',
                  width: '100%',
                  px: 1,
                  py: 1,
                  borderRadius: '14px',
                  color: isActive ? '#fff' : 'rgba(255,255,255,0.45)',
                  backgroundColor: isActive ? '#4f46e5' : 'transparent',
                  boxShadow: isActive ? '0 4px 12px rgba(79,70,229,0.4)' : 'none',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: isActive ? '#4f46e5' : 'rgba(255,255,255,0.1)',
                    color: '#fff',
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                <Badge
                  badgeContent={showBadge ? moduleBadgeCount : 0}
                  invisible={!showBadge}
                  color="error"
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    cursor: 'pointer',
                    color:
                      activeModuleName === mod.name
                        ? '#fff'
                        : 'rgba(255, 255, 255, 0.45)',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      color: '#fff',
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: '16px',
                      backgroundColor:
                        activeModuleName === mod.name
                          ? '#4f46e5'
                          : 'transparent',
                      boxShadow:
                        activeModuleName === mod.name
                          ? '0 4px 12px rgba(79, 70, 229, 0.4)'
                          : 'none',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      width: 44,
                      height: 44,
                      mb: 0.5,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.12)',
                      },
                    }}
                  >
                    <ModuleIcon sx={{ fontSize: 24 }} />
                  </Box>
                  <Typography variant="caption" sx={{ fontSize: 10, fontWeight: 500, lineHeight: 1 }}>
                    {SIDEBAR_MODULE_SHORT_TITLE[mod.name] || mod.name}
                  </Typography>
                </Badge>
                </Box>
              </Tooltip>
            );
          })}
        </Box>
        <Divider
          sx={{ width: '40%', my: 2, borderColor: 'rgba(255,255,255,0.1)' }}
        />
        <Tooltip title="Sign Out" placement="right">
          <Box
            onClick={onSignOut}
            sx={{
              p: 1.5,
              cursor: 'pointer',
              color: 'rgba(255, 255, 255, 0.5)',
              transition: 'color 0.2s',
              '&:hover': { color: '#ef4444' },
            }}
          >
            <i
              className="fas fa-sign-out-alt"
              style={{ fontSize: '1.4rem' }}
            ></i>
          </Box>
        </Tooltip>
      </Box>

      {/* Secondary rail — ข้อความอย่างเดียว ไม่มีไอคอนรายการ */}
      <Box
        sx={{
          width: 240,
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: '#fff',
          borderRight: '1px solid rgba(0, 0, 0, 0.05)',
        }}
      >
        <Box sx={{ p: 3, pb: 2, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box>
            <Typography
              variant="h6"
              color="primary.dark"
              sx={{ fontWeight: 700, letterSpacing: 0.5 }}
            >
              {activeGroup?.title || 'Menu'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {departmentName}
            </Typography>
          </Box>
          <Button
            onClick={toggleSidebar}
            startIcon={<KeyboardDoubleArrowLeftIcon />}
            size="small"
            sx={{
              mt: 0.25,
              flexShrink: 0,
              backgroundColor: '#4f46e5',
              color: '#fff',
              fontWeight: 700,
              fontSize: '0.72rem',
              borderRadius: '20px',
              px: 1.5,
              boxShadow: '0 2px 8px rgba(79,70,229,0.4)',
              '&:hover': { backgroundColor: '#3730a3' },
            }}
          >
            ซ่อนเมนู
          </Button>
        </Box>
        <Divider sx={{ mb: 1, mx: 3 }} />
        <Box sx={{ flexGrow: 1, overflowY: 'auto', px: 2, pb: 2 }}>
          <List disablePadding>
            {(activeGroup?.items || [])
              .reduce((acc, item) => {
                if (item.sub && item.sub.length > 0) {
                  item.sub.forEach((subItem) => {
                    if (
                      accessRightSubMenu(
                        me,
                        item.name,
                        subItem.href,
                        subItem.level ?? 0,
                      )
                    ) {
                      acc.push({
                        ...subItem,
                        moduleName: item.name,
                      });
                    }
                  });
                } else if (
                  accessRightSubMenu(me, item.name, item.href, 0) &&
                  !(item.href === '/profile/leave' && me?.userData?.type === 'DAILY')
                ) {
                  acc.push({ ...item, moduleName: item.name });
                }
                return acc;
              }, [])
              .map((item, index) => (
                <div key={item.title + index}>
                  <NavItem
                    href={item.href}
                    title={item.title}
                    sub={item.sub}
                    me={me}
                    moduleName={item.moduleName}
                    highlight={item.highlight}
                    badge={
                      item.href === '/humen/leave-request'
                        ? pendingLeaveCount
                        : item.href === '/humen/ot-request'
                        ? pendingOtCount
                        : item.href === '/humen/timestamp-approval'
                        ? pendingTimestampApprovalCount
                        : item.href === '/finance/expenses'
                        ? pendingExpensesCount
                        : item.href === '/humen/employee'
                        ? pendingEmployeeCount
                        : 0
                    }
                  />
                </div>
              ))}
          </List>
        </Box>
      </Box>
    </Box>
  );

  return (
    <div>
      <Hidden lgUp>
        <Drawer
          anchor="left"
          onClose={onMobileClose}
          open={openMobile}
          variant="temporary"
          PaperProps={{
            sx: {
              width: 320,
              border: 'none',
            },
          }}
        >
          {content}
        </Drawer>
      </Hidden>
      <Hidden lgDown>
        <Drawer
          anchor="left"
          open={!collapsed}
          variant="persistent"
          PaperProps={{
            sx: {
              width: 320,
              border: 'none',
              height: '100vh',
              top: 0,
            },
          }}
        >
          {content}
        </Drawer>
        {collapsed && (
          <Button
            onClick={toggleSidebar}
            startIcon={<KeyboardDoubleArrowRightIcon />}
            sx={{
              position: 'fixed',
              top: 20,
              left: 16,
              zIndex: 1201,
              backgroundColor: '#4f46e5',
              color: '#fff',
              fontWeight: 700,
              fontSize: '0.8rem',
              borderRadius: '24px',
              px: 2,
              py: 1,
              boxShadow: '0 4px 14px rgba(79,70,229,0.45)',
              '&:hover': { backgroundColor: '#3730a3' },
            }}
          >
            แสดงเมนู
          </Button>
        )}
      </Hidden>
    </div>
  );
};

DashboardSidebar.propTypes = {
  onMobileClose: PropTypes.func,
  openMobile: PropTypes.bool,
  me: PropTypes.object,
};

DashboardSidebar.defaultProps = {
  onMobileClose: () => {},
  openMobile: false,
};

export default DashboardSidebar;
